import { ISLANDS } from './voyage.js';

const SEAL_IDS = ['spring', 'grove', 'ruins'];
const STONE_ORDER = ['tide', 'leaf', 'sun'];
const SYMBOL_NAMES = { tide: '潮', leaf: '叶', sun: '日' };
const SEAL_NAMES = { spring: '泉水', grove: '椰林', ruins: '遗迹' };
const sites = [
  { id: 'spring-survey', islandId: 'spring', label: '泉水铭石', x: 2.5, z: 1.8, kind: 'survey' },
  { id: 'grove-survey', islandId: 'grove', label: '海风铃贝', x: 2.5, z: .5, kind: 'survey' },
  { id: 'ruins-clue', islandId: 'ruins', label: '古老碑文', x: 0, z: 4, kind: 'clue' },
  { id: 'ruins-tide', islandId: 'ruins', label: '潮之石', x: -3, z: 1.8, kind: 'stone', symbol: 'tide' },
  { id: 'ruins-leaf', islandId: 'ruins', label: '叶之石', x: 0, z: 2, kind: 'stone', symbol: 'leaf' },
  { id: 'ruins-sun', islandId: 'ruins', label: '日之石', x: 3, z: 1.8, kind: 'stone', symbol: 'sun' },
  { id: 'beacon-ignite', islandId: 'beacon', label: '沉睡的航标', x: 1.8, z: -1.3, kind: 'ignite' },
];

// Coordinates are shared by the model, walking target and interaction check.
export const QUEST_SITES = Object.freeze(sites.map((site) => {
  const island = ISLANDS.find((entry) => entry.id === site.islandId);
  return Object.freeze({ ...site, x: island.x + site.x, z: island.z + site.z });
}));

export function normalizeBeaconQuest(value) {
  const raw = value && typeof value === 'object' && !Array.isArray(value) ? value : {};
  const seals = SEAL_IDS.filter((id) => Array.isArray(raw.seals) && raw.seals.includes(id));
  const ruinsSequence = [];
  if (Array.isArray(raw.ruinsSequence)) {
    for (let i = 0; i < STONE_ORDER.length; i++) {
      if (raw.ruinsSequence[i] !== STONE_ORDER[i]) break;
      ruinsSequence.push(STONE_ORDER[i]);
    }
  }
  return { seals, ruinsSequence, lit: raw.lit === true && seals.length === SEAL_IDS.length };
}

export function questSummary(state) {
  const quest = normalizeBeaconQuest(state?.beaconQuest);
  const nextIslandId = quest.lit ? null : SEAL_IDS.find((id) => !quest.seals.includes(id)) || 'beacon';
  const text = quest.lit ? '航标已经点亮。群岛间的归途有了光。'
    : nextIslandId === 'beacon' ? '三枚印记已集齐，前往灯塔岛点亮航标。'
      : nextIslandId === 'ruins' ? '前往遗迹岛，观察碑文与三块符文石。'
        : nextIslandId === 'grove' ? '循着椰林风铃，寻找航标印记。'
          : '探索泉眼岛的铭石，寻找航标印记。';
  return { title: '寻回航标', text, nextIslandId, seals: quest.seals.length, total: 3, lit: quest.lit };
}

export function questSiteHint(state, siteId) {
  const site = QUEST_SITES.find((entry) => entry.id === siteId);
  if (!site) return '';
  const quest = normalizeBeaconQuest(state?.beaconQuest);
  if (site.id === 'grove-survey') return quest.seals.includes('grove') ? '查看风铃贝' : '倾听风铃贝';
  if (site.kind === 'survey') return quest.seals.includes(site.islandId) ? '查看铭石' : '调查铭石';
  if (site.kind === 'clue') return '阅读碑文';
  if (site.kind === 'stone') return quest.seals.includes('ruins') ? '查看符文石' : `触碰${SYMBOL_NAMES[site.symbol]}之石`;
  return quest.lit ? '查看航标' : quest.seals.length === 3 ? '点亮航标' : `查看航标 · 印记 ${quest.seals.length}/3`;
}

export function interactQuestSite(state, siteId) {
  const site = QUEST_SITES.find((entry) => entry.id === siteId);
  if (!site) return { ok: false, message: '这里没有航标遗迹', changed: false };
  const voyage = state?.voyage;
  if (voyage?.mode !== 'ashore' || voyage.islandId !== site.islandId)
    return { ok: false, message: '请先靠岸下船探索', changed: false };
  const distance = Math.hypot(voyage.walkX - site.x, voyage.walkZ - site.z);
  if (!Number.isFinite(distance) || distance > 2)
    return { ok: false, message: '请走近后再调查', changed: false };

  const quest = normalizeBeaconQuest(state.beaconQuest);
  if (site.kind === 'clue') return {
    ok: true, message: '碑文：潮水滋养树叶，树叶迎向太阳。依次触碰「潮 → 叶 → 日」。', changed: false,
  };
  if (site.kind === 'survey') {
    if (quest.seals.includes(site.islandId)) return { ok: true,
      message: site.islandId === 'grove' ? '海风穿过风铃贝，椰林印记已记录。' : `${SEAL_NAMES[site.islandId]}印记已记录，铭石仍泛着微光。`, changed: false };
    quest.seals = SEAL_IDS.filter((id) => id === site.islandId || quest.seals.includes(id));
    state.beaconQuest = quest;
    return { ok: true, message: `${site.islandId === 'grove' ? '风铃贝回应海风 · ' : ''}寻得${SEAL_NAMES[site.islandId]}印记 · ${quest.seals.length}/3`, changed: true };
  }
  if (site.kind === 'stone') {
    if (quest.seals.includes('ruins')) return { ok: true, message: '遗迹印记已记录，三块符文石的光彼此呼应。', changed: false };
    if (site.symbol !== STONE_ORDER[quest.ruinsSequence.length]) {
      const changed = quest.ruinsSequence.length > 0;
      quest.ruinsSequence = [];
      if (changed) state.beaconQuest = quest;
      return { ok: true, message: '符文的光熄灭了。可以重新尝试，碑文就在附近。', changed };
    }
    quest.ruinsSequence.push(site.symbol);
    if (quest.ruinsSequence.length === STONE_ORDER.length) {
      quest.seals = SEAL_IDS.filter((id) => id === 'ruins' || quest.seals.includes(id));
      state.beaconQuest = quest;
      return { ok: true, message: `三石共鸣！寻得遗迹印记 · ${quest.seals.length}/3`, changed: true };
    }
    state.beaconQuest = quest;
    return { ok: true, message: `${SYMBOL_NAMES[site.symbol]}之石亮起 · ${quest.ruinsSequence.length}/3`, changed: true };
  }
  if (quest.lit) return { ok: true, message: '航标已经点亮，海上的归途有了光。', changed: false };
  if (quest.seals.length !== SEAL_IDS.length) return {
    ok: false, message: `航标需要三枚印记 · 已寻得 ${quest.seals.length}/3`, changed: false,
  };
  quest.lit = true;
  state.beaconQuest = quest;
  state.gold += 80;
  return { ok: true, message: '航标点亮！群岛的归途重现 · 获得 80 金币', changed: true, completed: true };
}
